/**
 * Shared location dropdown logic for base-toolbar (campaign/segments) and appointments-toolbar.
 * Use locationToggle, locationApplyResult, and locationCancelRestoreTemp to avoid duplicating
 * onLocationToggle / onLocationApply / onLocationCancel implementations.
 */

export interface LocationOption {
  value: string;
  label: string;
  selected?: boolean;
}

export function locationToggle(tempSelected: Set<string>, locationValue: string): void {
  if (tempSelected.has(locationValue)) {
    tempSelected.delete(locationValue);
  } else {
    tempSelected.add(locationValue);
  }
}

export interface LocationApplyResult {
  selectedLocation: string;
  selectedLocationLabels: string[];
}

/**
 * Compute apply result from current temp selection and update locations[].selected.
 * Caller should set selectedLocation, selectedLocationLabels, emit locationChanged, and close dropdown.
 */
export function locationApplyResult(
  tempSelected: Set<string>,
  locations: LocationOption[]
): LocationApplyResult {
  const selectedArray = Array.from(tempSelected);
  if (selectedArray.length === 0) {
    locations.forEach(loc => { loc.selected = false; });
    return { selectedLocation: '', selectedLocationLabels: [] };
  }
  locations.forEach(loc => {
    loc.selected = selectedArray.includes(loc.value);
  });
  const selectedLocationLabels = selectedArray.map(id => {
    const loc = locations.find(l => l.value === id);
    return loc ? loc.label : id;
  });
  return {
    selectedLocation: selectedArray[0],
    selectedLocationLabels
  };
}

/**
 * Build the Set to restore into tempSelectedLocations on cancel.
 * When resolveByLabel is true, selectedLocation may be an ID or a label (appointments-toolbar).
 */
export function locationCancelRestoreTemp(
  selectedLocation: string,
  locations: LocationOption[],
  resolveByLabel = false
): Set<string> {
  const next = new Set<string>();
  if (!selectedLocation) return next;
  if (resolveByLabel) {
    const resolved = locations.find(
      l => l.value === selectedLocation || l.label === selectedLocation
    );
    if (resolved) next.add(resolved.value);
    else next.add(selectedLocation);
  } else {
    next.add(selectedLocation);
  }
  return next;
}
